import { CommonModule } from '@angular/common';
import { Component, HostListener, OnInit, effect, inject } from '@angular/core';
import { NavigationEnd, Router, RouterOutlet } from '@angular/router';
import { ErrorStatusService } from '@app/services/error-status.service';
import { EventTrackerService, QiraEventHumanReadable } from '@app/services/event-tracker.service';
import { GoogleTagService } from '@app/services/google-tag-manager.service';
import { MenuStateService } from '@app/services/menu-state.service';
import { NavigationService } from '@app/services/navigation.service';
import { VersionService } from '@app/services/version.service';
import { VoiceService } from '@app/services/voice.service';
import { TrialEndBannerComponent } from '@app/components/banner/trial-end-banner/trial-end-banner.component';
import { ErrorDialogComponent } from '@app/components/error-dialog/error-dialog.component';
import { ModalComponent } from '@app/components/modal/modal.component';
import { TechIssuesBannerComponent } from './components/banner/tech-issues-banner/tech-issues-banner.component';
import { ModalService } from './components/modal/modal.service';
import { HeaderComponent } from './layout/header.component';
import { environment } from '@env';
import Hotjar from '@hotjar/browser';
import { Toast } from 'primeng/toast';

@Component({
  selector: 'app-root',
  imports: [CommonModule, RouterOutlet, CommonModule, HeaderComponent, ModalComponent, TrialEndBannerComponent, Toast, TechIssuesBannerComponent],
  providers: [MenuStateService],
  templateUrl: './app.component.html',
  standalone: true,
  styleUrl: './app.component.scss',
})
export class AppComponent implements OnInit {
  title = 'qira-2';

  versionService = inject(VersionService);
  menuStateService = inject(MenuStateService);
  voiceService = inject(VoiceService);
  errorStatusService = inject(ErrorStatusService);
  modalService = inject(ModalService);
  router = inject(Router);
  navigationService = inject(NavigationService);
  gtmService = inject(GoogleTagService);
  eventTracker = inject(EventTrackerService);

  initErrorObserver = effect(() => {
    if (this.errorStatusService.globalFatalError()) {
      this.modalService.open(ErrorDialogComponent);
    }
  });

  constructor() {
    this.navigationService.init();
  }

  async ngOnInit() {
    this.registerHotJar();
    await this.registerGoogleTagManagerAndTrackRouterEvents();
  }

  private registerHotJar() {
    if (environment.HOTJAR_ID) {
      Hotjar.init(environment.HOTJAR_ID, environment.HOTJAR_VERSION);
    }
  }

  async registerGoogleTagManagerAndTrackRouterEvents() {
    if (environment.enableEventTracking) {
      await this.gtmService.init();

      // Track page routing events with GTM and event tracker
      await this.router.events.forEach((item) => {
        if (item instanceof NavigationEnd) {
          this.gtmService.push(QiraEventHumanReadable.PAGE_ROUTING, {
            url: item.url,
          });
          this.eventTracker.logEvent(QiraEventHumanReadable.PAGE_ROUTING, {
            url: item.url,
          });
        }
      });
    }
  }

  @HostListener('document:keydown', ['$event'])
  handleTheKeyboardEvent(event: KeyboardEvent) {
    if (event.code === 'Space') {
      this.voiceService.toggleAudio();
    }
  }
}
